// src/pages/admin/OrdersPage.jsx
import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import {
  Search, ShoppingBag, TrendingUp, Clock, Package, Truck,
  CheckCircle, XCircle, CreditCard, AlertTriangle, ChevronRight,
  Calendar,
} from 'lucide-react'
import { adminOrdersApi } from '@/api/services'
import { useCurrency } from '@/components/common/CurrencySwitcher'
import { Card, CardBody, StatusBadge, PageSpinner, Empty, Badge } from '@/components/common/ui'

const STATUS_TABS = [
  { key: '',                label: 'All',            icon: ShoppingBag },
  { key: 'pending_payment', label: 'Pending Pay',    icon: CreditCard },
  { key: 'confirmed',       label: 'Confirmed',      icon: CheckCircle },
  { key: 'processing',      label: 'Processing',     icon: Clock },
  { key: 'shipped',         label: 'Shipped',         icon: Truck },
  { key: 'delivered',       label: 'Delivered',       icon: Package },
  { key: 'cancelled',       label: 'Cancelled',       icon: XCircle },
]

// SLA thresholds in hours per status
const SLA_HOURS = { confirmed: 24, processing: 48, ready_to_ship: 24 }

function getSlaStatus(order) {
  const hours = SLA_HOURS[order.status]
  if (!hours) return null
  const updatedAt = new Date(order.updated_at)
  const deadline = new Date(updatedAt.getTime() + hours * 3600000)
  const remaining = (deadline - new Date()) / 3600000
  return {
    remaining: Math.round(remaining * 10) / 10,
    breached: remaining <= 0,
    urgent: remaining > 0 && remaining <= 6,
  }
}

export default function AdminOrdersPage() {
  const [status, setStatus] = useState('')
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(1)
  const [dateFrom, setDateFrom] = useState('')
  const [dateTo, setDateTo] = useState('')
  const { format } = useCurrency()

  const { data, isLoading } = useQuery({
    queryKey: ['admin-orders', status, search, page, dateFrom, dateTo],
    queryFn: () => adminOrdersApi.list({
      status: status || undefined,
      search: search || undefined,
      page,
      date_from: dateFrom || undefined,
      date_to: dateTo || undefined,
    }),
  })

  const payload  = data?.data?.data || {}
  const orders   = payload?.orders?.data || []
  const counts   = payload?.counts || {}
  const stats    = payload?.stats || {}
  const lastPage = payload?.orders?.last_page || 1
  const totalAll = counts.all || 0

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-xl font-display font-bold flex items-center gap-2">
          <ShoppingBag size={20} className="text-pink" /> Order Management
        </h1>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <div className="bg-white border border-border rounded-xl p-4">
          <div className="flex items-center gap-2 mb-1">
            <TrendingUp size={14} className="text-pink" />
            <p className="text-[10px] font-bold text-muted uppercase tracking-wider">GMV Today</p>
          </div>
          <p className="text-xl font-extrabold text-gray-900">{format(stats.gmv_today || 0)}</p>
          <p className="text-[10px] text-muted">{stats.orders_today || 0} orders</p>
        </div>
        <div className="bg-white border border-border rounded-xl p-4">
          <div className="flex items-center gap-2 mb-1">
            <TrendingUp size={14} className="text-green-600" />
            <p className="text-[10px] font-bold text-muted uppercase tracking-wider">GMV This Month</p>
          </div>
          <p className="text-xl font-extrabold text-green-600">{format(stats.gmv_month || 0)}</p>
          <p className="text-[10px] text-muted">{stats.orders_month || 0} orders</p>
        </div>
        <div className={`border rounded-xl p-4 ${stats.sla_breached > 0 ? 'bg-red-50 border-red-200' : 'bg-white border-border'}`}>
          <div className="flex items-center gap-2 mb-1">
            <AlertTriangle size={14} className={stats.sla_breached > 0 ? 'text-red-600' : 'text-gray-400'} />
            <p className="text-[10px] font-bold text-muted uppercase tracking-wider">SLA Breached</p>
          </div>
          <p className={`text-xl font-extrabold ${stats.sla_breached > 0 ? 'text-red-600' : 'text-gray-400'}`}>{stats.sla_breached || 0}</p>
          <p className="text-[10px] text-muted">overdue orders</p>
        </div>
        <div className="bg-white border border-border rounded-xl p-4">
          <div className="flex items-center gap-2 mb-1">
            <Clock size={14} className="text-amber-500" />
            <p className="text-[10px] font-bold text-muted uppercase tracking-wider">Active</p>
          </div>
          <p className="text-xl font-extrabold text-amber-500">{stats.active_orders || 0}</p>
          <p className="text-[10px] text-muted">in progress</p>
        </div>
      </div>

      {/* Status Tabs */}
      <div className="bg-white border border-border rounded-2xl p-3 mb-5">
        <div className="flex items-center gap-1 overflow-x-auto">
          {STATUS_TABS.map(t => {
            const count = t.key === '' ? totalAll : (counts[t.key] || 0)
            const Icon = t.icon
            const isActive = status === t.key
            return (
              <button key={t.key} onClick={() => { setStatus(t.key); setPage(1) }}
                className={`flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-bold whitespace-nowrap transition-all ${
                  isActive ? 'bg-pink text-white' : 'text-muted hover:bg-surface'
                }`}>
                <Icon size={13} />
                {t.label}
                {count > 0 && (
                  <span className={`ml-1 px-1.5 py-0.5 rounded-full text-[10px] font-extrabold ${
                    isActive ? 'bg-white/20' : 'bg-surface'
                  }`}>
                    {count}
                  </span>
                )}
              </button>
            )
          })}
        </div>
      </div>

      {/* Search + Date Filters */}
      <div className="flex items-end gap-3 mb-4 flex-wrap">
        <div className="relative max-w-sm flex-1">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted" />
          <input className="input pl-9 w-full" placeholder="Search order, buyer, or supplier…" value={search}
            onChange={e => { setSearch(e.target.value); setPage(1) }} />
        </div>
        <div className="flex items-center gap-2">
          <Calendar size={14} className="text-muted" />
          <input type="date" className="input text-xs" value={dateFrom}
            onChange={e => { setDateFrom(e.target.value); setPage(1) }} />
          <span className="text-xs text-muted">to</span>
          <input type="date" className="input text-xs" value={dateTo}
            onChange={e => { setDateTo(e.target.value); setPage(1) }} />
          {(dateFrom || dateTo) && (
            <button onClick={() => { setDateFrom(''); setDateTo(''); setPage(1) }}
              className="text-xs text-pink font-bold hover:underline">Clear</button>
          )}
        </div>
      </div>

      {/* Loading */}
      {isLoading && <PageSpinner />}

      {/* Empty */}
      {!isLoading && orders.length === 0 && (
        <Empty icon="📦" title="No orders found"
          description={status ? 'No orders match this filter.' : 'Orders will appear here as they are placed.'} />
      )}

      {/* Order Cards */}
      <div className="space-y-3">
        {orders.map(order => {
          const sla = getSlaStatus(order)
          const isShipped = ['shipped', 'out_for_delivery'].includes(order.status)
          return (
            <Link key={order.id} to={`/admin/orders/${order.id}`} className="block">
              <Card className={`hover:shadow-card-md hover:border-pink/30 transition-all ${
                sla?.breached ? 'border-red-300 bg-red-50/30' : sla?.urgent ? 'border-amber-300 bg-amber-50/20' : ''
              }`}>
                <CardBody>
                  <div className="flex items-center gap-4">
                    {/* Icon */}
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
                      sla?.breached ? 'bg-red-100' : sla?.urgent ? 'bg-amber-100' : 'bg-pink/10'
                    }`}>
                      <Package size={18} className={
                        sla?.breached ? 'text-red-600' : sla?.urgent ? 'text-amber-600' : 'text-pink'
                      } />
                    </div>

                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap mb-0.5">
                        <span className="font-bold text-sm text-gray-900">{order.order_number}</span>
                        <StatusBadge status={order.status} />
                        {sla?.breached && (
                          <Badge className="bg-red-100 text-red-700 text-[10px] flex items-center gap-1">
                            <AlertTriangle size={10} /> SLA Breached
                          </Badge>
                        )}
                        {sla?.urgent && !sla?.breached && (
                          <Badge className="bg-amber-100 text-amber-700 text-[10px] flex items-center gap-1">
                            <Clock size={10} /> {Math.round(sla.remaining)}h left
                          </Badge>
                        )}
                      </div>
                      <div className="flex items-center gap-1.5 text-xs text-muted">
                        <span className="font-semibold text-gray-700">{order.buyer?.name}</span>
                        <span className="text-gray-300">→</span>
                        <span className="font-semibold text-gray-700">{order.supplier?.business_name || order.supplier?.name}</span>
                        <span className="text-gray-300">·</span>
                        <span>{new Date(order.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}</span>
                        <span className="text-gray-300">·</span>
                        <span>{order.items?.length ?? 0} item{(order.items?.length ?? 0) !== 1 ? 's' : ''}</span>
                      </div>

                      {/* Tracking inline */}
                      {isShipped && order.tracking_number && (
                        <div className="flex items-center gap-1.5 mt-1.5 text-[11px] text-indigo-600">
                          <Truck size={12} />
                          <span>{order.courier_name} — <span className="font-mono font-bold">{order.tracking_number}</span></span>
                        </div>
                      )}
                    </div>

                    {/* Amount + Action */}
                    <div className="text-right flex-shrink-0">
                      <p className="font-extrabold text-sm text-gray-900">{format(order.total_amount)}</p>
                      <span className="text-xs text-pink font-bold flex items-center gap-0.5 justify-end mt-1">
                        Manage <ChevronRight size={12} />
                      </span>
                    </div>
                  </div>
                </CardBody>
              </Card>
            </Link>
          )
        })}
      </div>

      {/* Pagination */}
      {lastPage > 1 && (
        <div className="flex items-center justify-center gap-3 mt-6">
          <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
            className="flex items-center gap-1 px-3 py-2 rounded-xl border border-border text-xs font-bold disabled:opacity-40 hover:border-pink transition-colors">
            Prev
          </button>
          <span className="text-xs text-muted font-semibold">Page {page} of {lastPage}</span>
          <button onClick={() => setPage(p => Math.min(lastPage, p + 1))} disabled={page === lastPage}
            className="flex items-center gap-1 px-3 py-2 rounded-xl border border-border text-xs font-bold disabled:opacity-40 hover:border-pink transition-colors">
            Next
          </button>
        </div>
      )}
    </div>
  )
}
